import type { CarePlan, Coding, Extension } from '@medplum/fhirtypes';
import {
  CARE_PLAN_CATEGORY_SYSTEM,
  CARE_PLAN_TOPIC_KIND_SYSTEM,
  CARE_PLAN_TOPIC_KINDS,
  type CarePlanTopicKind,
  EXTENSIONS,
} from './constants';
import type { CarePlanCategoryCode } from './types';

const isCarePlanCategoryCode = (code: string | undefined): code is CarePlanCategoryCode =>
  code === 'watchlist' || code === 'vitality';

/**
 * The care plan category (`watchlist` | `vitality`) from a CarePlan's `category` codings, wherever
 * they sit: `category` is `0..*` and each entry's `coding` is `0..*`, so neither index is a contract.
 * Prefers a coding on the Liza system; falls back only to a coding with **no** system carrying a
 * known code, because rows migrated from Postgres in March 2026 may carry the code bare. A known
 * code on some other system is not ours and is not matched.
 */
export function getCarePlanCategory(cp: CarePlan): CarePlanCategoryCode | null {
  const codings: Coding[] = (cp.category ?? []).flatMap((c) => c.coding ?? []);
  const onSystem = codings.find((c) => c.system === CARE_PLAN_CATEGORY_SYSTEM)?.code;
  if (isCarePlanCategoryCode(onSystem)) return onSystem;
  const bare = codings.find((c) => c.system === undefined && isCarePlanCategoryCode(c.code))?.code;
  return isCarePlanCategoryCode(bare) ? bare : null;
}

const isCarePlanTopicKind = (code: string | undefined): code is CarePlanTopicKind =>
  (CARE_PLAN_TOPIC_KINDS as readonly string[]).includes(code ?? '');

/**
 * The kind of topic, from the `care-plan-topic-kind` coding on `category`, or `null` for an ordinary
 * topic. This is how a reader tells the Prevention topic from a topic somebody built: the coding is
 * on the resource itself, so the same question can be asked of the server (`category:not=…`).
 */
export function getCarePlanTopicKind(cp: CarePlan): CarePlanTopicKind | null {
  const code = (cp.category ?? [])
    .flatMap((c) => c.coding ?? [])
    .find((c) => c.system === CARE_PLAN_TOPIC_KIND_SYSTEM)?.code;
  return isCarePlanTopicKind(code) ? code : null;
}

/** The `category` coding that marks a topic as being of `kind`. */
export const carePlanTopicKindCoding = (kind: CarePlanTopicKind): Coding => ({
  system: CARE_PLAN_TOPIC_KIND_SYSTEM,
  code: kind,
});

/**
 * The statuses a topic (child CarePlan) still shows up under. `on-hold` is a topic made inactive: it
 * stays on the plan, badged and sorted after the active ones. Every topic reader searches both, so an
 * inactive topic still loads. These are also the two statuses `setTopicStatus` accepts — a topic is
 * only ever toggled between them. Split the settable set out if a visible-but-not-settable status
 * (a terminal one, say) ever joins the list.
 */
export const CARE_PLAN_TOPIC_VISIBLE_STATUSES = ['active', 'on-hold'] as const satisfies readonly CarePlan['status'][];
export type CarePlanTopicStatus = (typeof CARE_PLAN_TOPIC_VISIBLE_STATUSES)[number];

/** The comma-joined `status` search value matching every visible topic status. */
export const CARE_PLAN_TOPIC_VISIBLE_STATUS_SEARCH: string = CARE_PLAN_TOPIC_VISIBLE_STATUSES.join(',');

export const isInactiveTopic = (cp: Pick<CarePlan, 'status'>): boolean => cp.status === 'on-hold';

// Master CarePlans are created with no `partOf`; child CarePlans are created with
// `partOf: [ref(masterCarePlan)]` (see ensureMasterCarePlan / createChildCarePlan).
// This is the structural invariant — independent of the (deprecating) category coding.
export function isMasterCarePlan(cp: CarePlan): boolean {
  return !cp.partOf?.length;
}

/**
 * Longest personal summary the editors accept — the same bound as the health summary, so a
 * machine-written summary can't exceed what a clinician is then allowed to save back.
 */
export const PERSONAL_SUMMARY_MAX_LENGTH = 5000;

/** The personal summary recorded on a master care plan, or undefined when none has been written. */
export const getPersonalSummary = (carePlan: Pick<CarePlan, 'extension'>): string | undefined =>
  carePlan.extension?.find((ext) => ext.url === EXTENSIONS.PERSONAL_SUMMARY)?.valueString;

/**
 * A care plan's extensions with the personal summary set to `text`, or removed when `text` is empty.
 * Every other extension is kept, so a summary write never clobbers an unrelated one.
 */
export const withPersonalSummary = (extensions: Extension[] | undefined, text: string): Extension[] => {
  const others = (extensions ?? []).filter((ext) => ext.url !== EXTENSIONS.PERSONAL_SUMMARY);
  return text ? [...others, { url: EXTENSIONS.PERSONAL_SUMMARY, valueString: text }] : others;
};
